import { City, Country, ICountry, IState, State } from 'country-state-city';

export interface CityOption {
  name: string;
  id: string;
}

export interface PaginatedResult<T> {
  data: T[];
  total: number;
}

export const getCountries = (): ICountry[] => Country.getAllCountries();

export const getCitiesOfCountry = (
  countryCode: string | undefined
): CityOption[] | undefined => {
  return countryCode
    ? City.getCitiesOfCountry(countryCode)?.map(
        ({ name, latitude, longitude }, index) => ({
          name,
          id: `${latitude}${longitude}${index}`,
        })
      )
    : undefined;
};

export const getStatesOfCountry = (
  countryCode: string | undefined
): IState[] | undefined => {
  return countryCode ? State.getStatesOfCountry(countryCode) : undefined;
};

export const getCitiesOfState = (
  countryCode: string | undefined,
  stateCode: string | undefined
): CityOption[] | undefined => {
  return countryCode && stateCode
    ? City.getCitiesOfState(countryCode, stateCode)?.map(
        ({ name, latitude, longitude }, index) => ({
          name,
          id: `${latitude}${longitude}${index}`,
        })
      )
    : undefined;
};

export const getPaginatedCountries = (
  search: string = '',
  page: number = 1,
  limit: number = 10
): PaginatedResult<ICountry> => {
  const allCountries = Country.getAllCountries();

  const filteredCountries = search
    ? allCountries.filter(country =>
        country.name.toLowerCase().includes(search.toLowerCase())
      )
    : allCountries;

  const total = filteredCountries.length;

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;

  const paginatedData = filteredCountries.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    total,
  };
};

export const getPaginatedCities = (
  countryCodes: string[],
  search: string = '',
  page: number = 1,
  limit: number = 10
): PaginatedResult<CityOption> => {
  if (!countryCodes.length) {
    return { data: [], total: 0 };
  }

  const allCities = countryCodes.flatMap(
    countryCode =>
      City.getCitiesOfCountry(countryCode)?.map(
        ({ name, latitude, longitude }, index) => ({
          name,
          id: `${latitude}${longitude}${index}`,
        })
      ) || []
  );

  const filteredCities = search
    ? allCities.filter(city =>
        city.name.toLowerCase().includes(search.toLowerCase())
      )
    : allCities;

  const total = filteredCities.length;

  const startIndex = (page - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedData = filteredCities.slice(startIndex, endIndex);

  return {
    data: paginatedData,
    total,
  };
};
